// eslint-disable-next-line jira/filename-case
import { createContext, useContext, type Provider } from 'react';
import {
	useCanManageViews,
	useManageViewPermissions,
} from '@jira/polaris__component-permissions-store/src/useCanManageViews.tsx';
import { isNavigationProject, type NavigationContainer } from '../common/types.tsx';
import type { ItemId } from '../common/constants/analytics/item-id.tsx';
import type {
	ContainerTypePermissions,
	GetViewUrl,
	JpdNavViewsContext,
	ViewsEntityLimitType,
	ViewsetsEntityLimitType,
} from './types.tsx';
import type { ViewParams } from './analytics/types.tsx';

const assertFunction = (text: string) => () => {
	throw new Error(`${text} must be implemented`);
};

const Context = createContext<JpdNavViewsContext<NavigationContainer>>({
	name: undefined,
	container: undefined,
	sectionsEnabled: false,
	containerTypePermissions: 'view',
	viewsEntityLimitType: undefined,
	viewsetsEntityLimitType: undefined,
	getViewUrl: assertFunction('getViewUrl'),
	useIsReadOnly: () => false,
	useAnalyticsL2: assertFunction('useAnalyticsL2'),
	useAnalyticsL3: assertFunction('useAnalyticsL3'),
	useAnalyticsL4: assertFunction('useAnalyticsL4'),
});

export const JpdNavViewsProvider: Provider<JpdNavViewsContext<NavigationContainer>> =
	Context.Provider;

export const useGetViewUrl = (): GetViewUrl<NavigationContainer> => {
	const { getViewUrl } = useContext(Context);
	return getViewUrl;
};

export const useAnalyticsL2 = (): {
	sendMeatBallDropDownOnProjectsAnalyticsEvent: (container: NavigationContainer) => void;
	sendViewNavigationItemInsideProjectsAnalyticsEvent: (options: {
		container: NavigationContainer;
		viewParams: ViewParams;
	}) => void;
	sendCreateViewOnViewNavigationItemAnalyticsEvent: (container: NavigationContainer) => void;
	sendCreateViewOnProjectMenuItemAnalyticsEvent: (container: NavigationContainer) => void;
} => {
	const { useAnalyticsL2: useAnalyticsL2Provided } = useContext(Context);

	return useAnalyticsL2Provided();
};

export const useAnalyticsL3 = (): {
	sendMeatBallDropDownOnProjectsMenuItemAnalyticsEvent: (options: {
		itemId: ItemId;
		container: NavigationContainer;
	}) => void;
	sendMeatBallDropDownOnViewsAnalyticsEvent: (options: {
		container: NavigationContainer;
		viewParams: ViewParams;
	}) => void;
	sendCreateViewDropDownOnProjectMenuItemAnalyticsEvent: (options: {
		itemId: ItemId;
		viewParams: ViewParams;
		container: NavigationContainer;
	}) => void;
} => {
	const { useAnalyticsL3: useAnalyticsL3Provided } = useContext(Context);

	return useAnalyticsL3Provided();
};

export const useAnalyticsL4 = (): {
	sendMeatBallDropDownOnViewsMenuItemAnalyticsEvent: (options: {
		itemId: ItemId;
		viewParams: ViewParams;
		container: NavigationContainer;
	}) => void;
} => {
	const { useAnalyticsL4: useAnalyticsL4Provided } = useContext(Context);

	return useAnalyticsL4Provided();
};

export const useJpdNavViewName = (): string | undefined => {
	const { name } = useContext(Context);
	return name;
};

export const useSectionsEnabled = (): boolean => {
	const { sectionsEnabled } = useContext(Context);
	return sectionsEnabled;
};

export const useContainerTypePermissions = (): ContainerTypePermissions => {
	const { containerTypePermissions } = useContext(Context);
	return containerTypePermissions;
};

export const useIsReadOnly = (container?: NavigationContainer): boolean => {
	const { useIsReadOnly: useIsReadOnlyProvided } = useContext(Context);

	return useIsReadOnlyProvided?.(container) ?? false;
};

export const useContainer = (): NavigationContainer => {
	const { container } = useContext(Context);

	if (!container) {
		throw new Error('Please provide `container` prop');
	}

	return container;
};

export const useContainerId = (): string => {
	const container = useContainer();

	if (isNavigationProject(container)) {
		return container.projectId;
	}

	return container.collectionUUID;
};

export const useJpdNavCanManageViews = (): boolean => {
	const container = useContainer();
	const containerTypePermissions = useContainerTypePermissions();
	const canManageViews = useCanManageViews();

	const containerId = isNavigationProject(container) ? undefined : container.collectionUUID;

	const { canManageView } = useManageViewPermissions(containerId);

	const isReadOnly = useIsReadOnly();

	if (containerTypePermissions === 'view') {
		return canManageViews && !isReadOnly;
	}

	return canManageView && !isReadOnly;
};

export const useViewsEntityLimitType = (): ViewsEntityLimitType => {
	const { viewsEntityLimitType } = useContext(Context);

	if (!viewsEntityLimitType) {
		throw new Error('Please provide entityLimitType as `viewsEntityLimitType` prop');
	}

	return viewsEntityLimitType;
};

export const useViewsetsEntityLimitType = (): ViewsetsEntityLimitType | undefined => {
	const { viewsetsEntityLimitType } = useContext(Context);
	return viewsetsEntityLimitType;
};
